import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FormProvider, useForm} from 'react-hook-form';
import {
  useGuestGroup,
  useMutateReservation,
  usePetFeeDeal,
  useReservationSettings,
  useTaxes,
} from 'hooks';
import {useModalOncePerSession, usePath} from 'hooks/common';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useGuestGroupMutation} from 'hooks/useGuestGroupMutation';
import {useComputedOnboardingDetails} from 'hooks/useComputedOnboardingDetails';
import {useOnboardingStore} from '../../store/useOnboardingStore';
import {PATHS} from 'routes/constants';
import {getAgeLimitByCountry, getFields, getGroupType} from 'utils/reservationSettings';
import {FORM_NAMES} from 'utils/reservationSettings/constants';
import {SHOWED_GUEST_GROUP_MODAL} from 'utils/constants';
import {StepInfoBox} from 'pages/Auth/components/StepInfoBox';
import {StyledNumberOfGuestsForm} from './styled';

type FormTypes = {
  [FORM_NAMES.junior]: number;
  [FORM_NAMES.middle]: number;
  [FORM_NAMES.senior]: number;
  [FORM_NAMES.others]: number;
  [FORM_NAMES.numberOfGuests]: number;
  [FORM_NAMES.numberOfPets]: number;
};
function NumberOfGuestsStep() {
  const navigate = useNavigate();
  const formMethods = useForm<FormTypes>();
  const {handleSubmit} = formMethods;
  const {getFullPath} = usePath();
  const {t} = useTranslation();
  const {seasonEntity, isFetching: isTaxesFetching} = useTaxes();
  const {housing, data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const {shouldAskListOfParticipants} = useComputedOnboardingDetails();
  const {toggleStorageKeyData} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_GUEST_GROUP_MODAL,
    trigger: 'manual',
  });
  const groupType = useOnboardingStore(state => state.groupType);

  const {
    guestGroup,
    isLoading: isGuestGroupLoading,
    isFetching: isGuestGroupFetching,
  } = useGuestGroup();
  const {updateGuestGroup: patchGuestGroup, isLoading: isGuestGroupMutation} =
    useGuestGroupMutation();
  const {mutate: updateReservation, isPending: isReservationMutation} =
    useMutateReservation();
  const {createPetFeeDeal, hasPetFeeOffer, isCreatingPetFeeDeal, isLoadingPetFeeOffer} =
    usePetFeeDeal();
  const {checkCanEditReservationDetails} = useReservationSettings();

  const isActiveTaxes = summary?.taxes?.active;
  const countryCode = summary?.country_code;

  const validateGuestAge = useMemo(() => {
    if (isActiveTaxes || !countryCode) {
      return {ageLimit: null};
    }

    return getAgeLimitByCountry(countryCode, housing?.police_account_type);
  }, [countryCode, isActiveTaxes, housing]);
  const canEdit = checkCanEditReservationDetails();

  const isLoading =
    isGuestGroupLoading ||
    isGuestGroupFetching ||
    isGuestGroupMutation ||
    isTaxesFetching ||
    isLoadingPetFeeOffer ||
    isCreatingPetFeeDeal ||
    isReservationMutation;

  const onSubmit = useCallback(
    (formData: FormTypes) => {
      const visibleFields = getFields({
        lessThanRule: seasonEntity.lessThanRule,
        betweenRule: seasonEntity.betweenRule,
        moreThanRule: seasonEntity.moreThanRule,
      });

      const senior = visibleFields[FORM_NAMES.senior] ? formData[FORM_NAMES.senior] : 0;
      const middle = visibleFields[FORM_NAMES.middle] ? formData[FORM_NAMES.middle] : 0;
      const junior = visibleFields[FORM_NAMES.junior] ? formData[FORM_NAMES.junior] : 0;
      const numberOfGuests = visibleFields[FORM_NAMES.numberOfGuests]
        ? formData[FORM_NAMES.numberOfGuests]
        : 0;

      const payload = {
        seniors: senior,
        adults: middle || numberOfGuests,
        children: junior,
        group_type: getGroupType(numberOfGuests, groupType),
      };

      patchGuestGroup(payload, {
        onSuccess: () => {
          const numberOfPets = formData[FORM_NAMES.numberOfPets] ?? 0;

          if (hasPetFeeOffer) {
            updateReservation({payload: {number_of_pets: numberOfPets}});
          }

          createPetFeeDeal(numberOfPets);
          toggleStorageKeyData(true);
          navigate(
            getFullPath(shouldAskListOfParticipants ? PATHS.onboarding.whoIsComing : '/'),
          );
        },
      });
    },
    [
      patchGuestGroup,
      seasonEntity,
      navigate,
      getFullPath,
      shouldAskListOfParticipants,
      groupType,
      toggleStorageKeyData,
      createPetFeeDeal,
      hasPetFeeOffer,
      updateReservation,
    ],
  );

  return (
    <>
      <StepInfoBox
        title={t('number_of_guests')}
        description={t('confirm_number_of_guests')}
      />
      <FormProvider {...formMethods}>
        <StyledNumberOfGuestsForm
          seasonEntity={seasonEntity}
          guestGroup={guestGroup}
          onSubmit={handleSubmit(onSubmit)}
          ageLimit={validateGuestAge.ageLimit}
          disabled={isLoading}
          isLoading={isTaxesFetching}
          canEdit={canEdit}
          showPetsCounter={hasPetFeeOffer}
          className="number-of-guests-form"
        />
      </FormProvider>
    </>
  );
}

export {NumberOfGuestsStep};
